"use client";

import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Flex,
  Icon,
  Indicator,
  Ruler,
  Spacing,
  Text,
  type UIAction,
  useActions,
  useHover,
  useThemeSlot,
} from "@huub-web/ui";
import type { ThemeSlotPropsMap } from "@huub-web/ui/contexts/ThemeSlotContext";
import { formatNumberAsMoney } from "@huub-web/utils";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";

type MaximumMortgageCardProps = {
  action?: UIAction;
  disabled?: boolean;
  maxMortgage?: number;
  monthlyCosts?: number;
  neededLoan?: number;
  neededMonthlyCosts?: number;
  purpose?: string;
};

/**
 * Default scope visual for the maximum-mortgage card — a calculator badge.
 * Overridden when the theme registers a `scopeVisual` slot.
 */
const MortgageScopeBadge: FC<ThemeSlotPropsMap["scopeVisual"]> = () => (
  <Indicator icon="calculator" type="square" size="l" />
);

const MaximumMortgageCard: FC<MaximumMortgageCardProps> = ({
  action,
  disabled,
  maxMortgage,
  monthlyCosts,
  neededLoan,
  neededMonthlyCosts,
  purpose,
}) => {
  const { t } = useTranslation();
  const { handleAction } = useActions();
  const { hasHover, handleEnter, handleLeave } = useHover({ disabled });

  const ScopeVisual = useThemeSlot({
    key: "scopeVisual",
    defaultComponent: MortgageScopeBadge,
  });

  const handleClick = () => {
    if (action) {
      handleAction(action);
    }
  };

  const isRenovate = purpose === "renovate";
  const displayAmount = isRenovate ? neededLoan : maxMortgage;
  const displayMonthlyCosts = isRenovate ? neededMonthlyCosts : monthlyCosts;

  return (
    <Card
      as="button"
      border="default"
      padding="l"
      disabled={disabled}
      onClick={handleClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <ScopeVisual scope="maximumMortgage" size={72} />
      {purpose && (
        <Text variant="surfaceSubtitle" color="currentColor50">
          {t(`maximumMortgageHubCard.purpose.${purpose}`)}
        </Text>
      )}
      <Text variant="bodyM">
        {t(
          isRenovate
            ? "maximumMortgageHubCard.completedTitleRenovate"
            : "maximumMortgageHubCard.completedTitle",
        )}
      </Text>
      {displayAmount !== undefined && (
        <Text variant="displayM" bold>
          {formatNumberAsMoney(displayAmount)}
        </Text>
      )}
      {displayMonthlyCosts !== undefined && (
        <>
          <Ruler />
          <Flex justify="space-between" gap="s">
            <Text variant="bodyM">
              {t("maximumMortgageHubCard.monthlyCosts")}
            </Text>
            <Text variant="bodyM" bold>
              {formatNumberAsMoney(displayMonthlyCosts)}
            </Text>
          </Flex>
        </>
      )}
      <Spacing grow />
      <Button
        as="div"
        variant="tertiary"
        size="s"
        trailing={<Icon icon="chevronRight" />}
        width="stretch"
        active={hasHover}
      >
        {t("maximumMortgageHubCard.cta")}
      </Button>
    </Card>
  );
};

export type MaximumMortgageHubCardTemplateProps = {
  /**
   * Optional override for the card title; falls back to i18n key
   */
  title?: string;
  /**
   * Optional override for the card description; falls back to i18n key
   */
  description?: string;
  /**
   * The action to perform when the card is clicked
   */
  action?: UIAction;
  /**
   * Whether the card is disabled
   */
  disabled?: boolean;
  /**
   * Maximum mortgage amount in euros
   */
  maxMortgage?: number;
  /**
   * Monthly mortgage costs in euros
   */
  monthlyCosts?: number;
  /**
   * Required loan amount in euros — used instead of maxMortgage for the renovate purpose
   */
  neededLoan?: number;
  /**
   * Required monthly costs for the needed loan — used instead of monthlyCosts for the renovate purpose
   */
  neededMonthlyCosts?: number;
  /**
   * Mortgage purpose — "finance", "refinance", or "renovate"
   */
  purpose?: string;
};

export const MaximumMortgageHubCardTemplate: FC<
  MaximumMortgageHubCardTemplateProps
> = ({
  title,
  description,
  action,
  disabled,
  maxMortgage,
  monthlyCosts,
  neededLoan,
  neededMonthlyCosts,
  purpose,
}) => {
  const { t } = useTranslation();
  const { lockPrompts, lockUI } = useTemplates();

  const resolvedTitle = title ?? t("maximumMortgageHubCard.title");
  const resolvedDescription =
    description ?? t("maximumMortgageHubCard.description");

  const contentItem = useMemo(
    () => ({
      type: "card" as const,
      label: resolvedTitle,
      description: resolvedDescription,
      meta: {
        ...(maxMortgage != null ? { maxMortgage } : {}),
        ...(monthlyCosts != null ? { monthlyCosts } : {}),
        ...(neededLoan != null ? { neededLoan } : {}),
        ...(neededMonthlyCosts != null ? { neededMonthlyCosts } : {}),
        ...(purpose ? { purpose } : {}),
      },
      ...(action ? { action } : {}),
    }),
    [
      resolvedTitle,
      resolvedDescription,
      action,
      maxMortgage,
      monthlyCosts,
      neededLoan,
      neededMonthlyCosts,
      purpose,
    ],
  );
  usePageContent("hubcard-maxMortgage", contentItem);

  const isDisabled = (() => {
    if (action?.type === "prompt") {
      return lockPrompts;
    }
    return lockUI || disabled;
  })();

  return (
    <MaximumMortgageCard
      action={action}
      disabled={isDisabled}
      maxMortgage={maxMortgage}
      monthlyCosts={monthlyCosts}
      neededLoan={neededLoan}
      neededMonthlyCosts={neededMonthlyCosts}
      purpose={purpose}
    />
  );
};
